import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { API_BASE_URL } from '../../config/api';
import {
  displayText, dayView, searchView, highlightParts, leafOf, longDay, monthName, joinNames, repeatCount, plural,
} from '../../utils/houseMemoryBook';

/**
 * 📖 "Older memories" — the Memory Wall's book (09-24, Nina: "why didn't it
 * grow?"). Every house day, a month at a time, plus a search across all of
 * them. Mockup: https://claude.ai/artifact/RTMSjQ4DiX4DaxrsLPvozP
 *
 * Lazy: fetches `houseMemoryArchive` only while open (one month per tab, a
 * search per typed query), never on a poll. Shared memories only, like the
 * wall. Portaled to <body>: the wall lives inside drawers and sheets.
 *
 * ⚠️ The house's ui_theme.customCSS styles EVERY button/input/heading on the
 * page (text-shadow, a hover lift, a focus glow). The .hma rules below are
 * doubled-class so they outrank those element rules in any source order.
 */

const CSS = `
.hma { --cork-top:#d2a878; --cork-bot:#b98c5c; --cork-edge:#8a5f33; --paper:#fffdf4; --paper-2:#f9f1df; --paper-3:#f1e3c4;
  --ink:#3b2a1a; --ink-faint:rgba(59,42,26,.72); --ink-soft:rgba(59,42,26,.52); --rule:rgba(178,143,74,.5); --pin:#b4412c;
  --mark:#ffe08a; --brass:#7a5326;
  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif; color:var(--ink); text-shadow:none; }
.hma *, .hma *::before, .hma *::after { box-sizing:border-box; }
.hma.hma button { font:inherit; color:inherit; text-shadow:none; transform:none; }
.hma.hma button:hover { transform:none; }
.hma.hma h2 { text-shadow:none; }
.hma.hma :focus-visible { outline:2px solid var(--pin); outline-offset:2px; }
.hma-scrim { position:fixed; inset:0; background:rgba(18,12,8,.62); z-index:100020; }
.hma-book { position:fixed; z-index:100021; top:50%; left:50%; transform:translate(-50%,-50%);
  width:min(740px, calc(100% - 32px)); height:min(86vh, 920px); display:flex; flex-direction:column;
  background:var(--paper); border:1px solid var(--cork-edge); border-radius:12px; overflow:hidden;
  box-shadow:0 24px 60px rgba(0,0,0,.55); }
.hma-head { display:flex; align-items:center; gap:10px; padding:12px 14px 10px;
  background:linear-gradient(180deg,var(--cork-top) 0%,var(--cork-bot) 100%); border-bottom:1px solid var(--cork-edge); }
.hma-head h2 { margin:0; flex:1; min-width:0; font-size:13px; font-weight:800; letter-spacing:.14em; text-transform:uppercase; color:var(--ink); }
.hma-meta { font-size:12px; color:var(--ink-faint); font-weight:600; letter-spacing:0; text-transform:none; }
.hma.hma .hma-x { border:none; background:none; cursor:pointer; font-size:16px; font-weight:800; padding:2px 6px; }
.hma-tools { padding:12px 14px 0; display:flex; flex-direction:column; gap:10px; }
.hma-search { display:flex; align-items:center; gap:8px; padding:8px 12px; border:1px solid var(--rule); border-radius:999px; background:#fff; }
.hma-search:focus-within { border-color:var(--pin); box-shadow:0 0 0 2px rgba(180,65,44,.18); }
.hma.hma .hma-search input { flex:1; min-width:0; border:none; outline:none; box-shadow:none; background:transparent; font:inherit; font-size:14px; color:var(--ink); padding:0; margin:0; }
.hma.hma .hma-search input:focus { border:none; outline:none; box-shadow:none; }
.hma-tabrow { display:flex; align-items:center; gap:6px; flex-wrap:wrap; border-bottom:1px solid var(--rule); }
.hma.hma .hma-tab { border:none; background:none; cursor:pointer; padding:8px 10px 9px; margin-bottom:-1px;
  font-size:13.5px; font-weight:800; color:var(--ink-faint); border-bottom:3px solid transparent; }
.hma-tab span { font-weight:600; font-size:12px; margin-left:4px; }
.hma.hma .hma-tab[aria-selected="true"] { color:var(--ink); border-bottom-color:var(--pin); }
.hma-tabs-off .hma-tab { opacity:.45; }
.hma-rep { margin-left:auto; display:flex; align-items:center; gap:6px; font-size:12px; color:var(--ink-faint); padding-bottom:4px; cursor:pointer; }
.hma.hma .hma-rep input, .hma.hma .hma-rep input:focus { accent-color:var(--pin); box-shadow:none; margin:0; }
.hma-days { position:relative; flex:1; min-height:0; overflow:auto; padding:4px 0 18px; }
.hma-summary { padding:12px 14px 4px; font-size:13px; color:var(--ink-faint); }
.hma.hma .hma-retry { margin-left:8px; border:1px solid var(--cork-edge); background:var(--paper-2); border-radius:999px; padding:3px 10px; cursor:pointer; font-size:12px; font-weight:700; }
.hma-day { border-bottom:1px solid var(--rule); }
.hma.hma .hma-row { width:100%; display:grid; grid-template-columns:46px minmax(0,1fr) auto 14px; gap:12px; align-items:center;
  padding:11px 14px; border:none; background:none; cursor:pointer; text-align:left; border-radius:0; box-shadow:none; }
.hma.hma .hma-row:hover, .hma.hma .hma-open > .hma-row { background:var(--paper-2); }
.hma-leaf { border:1px solid var(--rule); border-radius:6px; overflow:hidden; background:#fff; text-align:center; box-shadow:0 1px 2px rgba(0,0,0,.12); }
.hma-leaf .mo { display:block; background:var(--pin); color:var(--paper); font-size:9.5px; font-weight:800; letter-spacing:.12em; padding:2px 0 1px; }
.hma-leaf .dn { display:block; font-size:19px; font-weight:800; line-height:1.15; font-variant-numeric:tabular-nums; padding-top:2px; }
.hma-leaf .wd { display:block; font-size:9px; font-weight:700; letter-spacing:.1em; color:var(--ink-soft); padding-bottom:3px; }
.hma-main { display:flex; flex-direction:column; gap:3px; min-width:0; }
.hma-hl { font-size:14px; font-weight:650; line-height:1.35; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; overflow-wrap:anywhere; }
.hma-open .hma-hl { -webkit-line-clamp:unset; display:block; }
.hma-tag { display:inline-block; font-size:10.5px; font-weight:800; letter-spacing:.06em; text-transform:uppercase; color:var(--brass);
  background:var(--paper-3); border-radius:4px; padding:1px 6px; margin-right:6px; vertical-align:1px; }
.hma-sub { font-size:12px; color:var(--ink-faint); overflow-wrap:anywhere; }
.hma-movein { color:var(--brass); font-weight:700; }
.hma-sofar { color:var(--pin); font-weight:700; }
.hma-count { text-align:right; font-variant-numeric:tabular-nums; }
.hma-count b { display:block; font-size:15px; }
.hma-count small { font-size:10.5px; color:var(--ink-faint); }
.hma-chev { font-size:18px; color:var(--ink-soft); transition:transform .15s ease; }
.hma-open .hma-chev { transform:rotate(90deg); }
.hma-lines { list-style:none; margin:0; padding:2px 14px 14px 72px; display:flex; flex-direction:column; gap:7px; }
.hma-lines li { font-size:13.5px; line-height:1.45; position:relative; padding-left:14px; overflow-wrap:anywhere; }
.hma-lines li::before { content:""; position:absolute; left:0; top:.62em; width:5px; height:5px; border-radius:50%; background:var(--cork-bot); }
.hma-lines li.ev { padding:6px 10px; border-radius:6px; background:var(--paper-3); border:1px solid var(--rule); font-weight:600; }
.hma-lines li.ev::before, .hma-lines li.who::before, .hma-lines li.note::before { display:none; }
.hma-lines li.who { color:var(--ink-faint); font-size:12.5px; padding-left:0; }
.hma-lines li.repeat { color:var(--ink-soft); }
.hma-lines li.repeat::after { content:"repeat"; margin-left:6px; font-size:10px; font-weight:800; letter-spacing:.08em; text-transform:uppercase; color:var(--ink-soft); }
.hma-lines li.note { font-size:12px; color:var(--ink-soft); padding-left:0; }
.hma mark { background:var(--mark); color:inherit; border-radius:2px; padding:0 1px; }
@media (max-width:720px) {
  .hma-book { top:0; left:0; transform:none; width:100%; height:100%; border-radius:0; border:none;
    padding-top:env(safe-area-inset-top,0px); padding-bottom:env(safe-area-inset-bottom,0px); }
  .hma.hma .hma-row { grid-template-columns:42px minmax(0,1fr) auto; gap:10px; padding:10px 12px; }
  .hma-chev { display:none; }
  .hma-lines { padding:2px 12px 14px 12px; }
  .hma-rep { margin-left:0; width:100%; padding:2px 0 8px; }
}
@media (prefers-reduced-motion:reduce) { .hma-chev { transition:none; } }
`;

function Highlight({ text, q }) {
  return highlightParts(text, q).map((p, i) => (p.hit ? <mark key={i}>{p.text}</mark> : <React.Fragment key={i}>{p.text}</React.Fragment>));
}

function DayBlock({ day, open, onToggle, q, reps }) {
  const v = dayView(day, { q, reps });
  const leaf = leafOf(day.day);
  const sub = [];
  if (day.so_far) sub.push(<span key="s" className="hma-sofar">today, so far</span>);
  if (day.moved_in && day.moved_in.length) sub.push(<span key="m" className="hma-movein">🏡 {joinNames(day.moved_in)} moved in</span>);
  if (day.visitors && day.visitors.length) {
    sub.push(<span key="v">👋 {day.visitors.slice(0, 3).map(([n]) => n).join(' · ')}{day.visitors.length > 3 ? ` +${day.visitors.length - 3}` : ''}</span>);
  }
  const listId = `hma-dl-${day.day}`;
  return (
    <div className={`hma-day${open ? ' hma-open' : ''}`}>
      <button type="button" className="hma-row" aria-expanded={open} aria-controls={listId} onClick={onToggle}>
        <span className="hma-leaf" aria-hidden="true"><span className="mo">{leaf.mo}</span><span className="dn">{leaf.dn}</span><span className="wd">{leaf.wd}</span></span>
        <span className="hma-main">
          <span className="hma-hl">
            {day.headline?.tag ? <span className="hma-tag">{day.headline.tag}</span> : null}
            {day.headline?.text || ''}
          </span>
          {sub.length > 0 && (
            <span className="hma-sub">{sub.reduce((acc, el, i) => (i ? [...acc, ' · ', el] : [el]), [])}</span>
          )}
        </span>
        <span className="hma-count"><b>{v.count}</b><small>{v.count === 1 ? 'memory' : 'memories'}</small></span>
        <span className="hma-chev" aria-hidden="true">›</span>
      </button>
      {open && (
        <ul className="hma-lines" id={listId} aria-label={longDay(day.day)}>
          {v.who.map(([name, n]) => (
            <li key={`w-${name}`} className="who">👋 <Highlight text={name} q={q} /> came by ({plural(n, 'message', 'messages')})</li>
          ))}
          {v.lines.map((l, i) => (
            <li key={i} className={l.e ? 'ev' : l.d ? 'repeat' : ''}>{l.e ? '🏠 ' : ''}<Highlight text={displayText(l)} q={q} /></li>
          ))}
          {v.hidden > 0 && <li className="note">{plural(v.hidden, 'repeat', 'repeats')} hidden</li>}
        </ul>
      )}
    </div>
  );
}

export default function HouseMemoryArchive({ gameId, onClose, returnFocusTo = null }) {
  const [months, setMonths] = useState(null);      // [{ month, days, memories }]
  const [meta, setMeta] = useState(null);          // { total, days, first_day }
  const [month, setMonth] = useState(null);
  const [byMonth, setByMonth] = useState({});      // month → days[]
  const [search, setSearch] = useState({ q: '', days: null, truncated: false, loading: false });
  const [q, setQ] = useState('');
  const [reps, setReps] = useState(false);
  const [open, setOpen] = useState(() => new Set());
  const [status, setStatus] = useState('loading'); // loading · ready · error
  const inputRef = useRef(null);
  const daysRef = useRef(null);
  const reqRef = useRef(0);

  const url = (params) => `${API_BASE_URL}/api/takeGameStep?houseMemoryArchive=true&gameInstanceId=${encodeURIComponent(gameId)}${params}`;

  const loadMonth = async (m) => {
    setStatus('loading');
    try {
      const res = await fetch(url(m ? `&month=${encodeURIComponent(m)}` : ''));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMonths(Array.isArray(data.months) ? data.months : []);
      if (data.summary) setMeta(data.summary);
      if (data.month) {
        setByMonth((b) => ({ ...b, [data.month]: Array.isArray(data.days) ? data.days : [] }));
        setMonth(data.month);
      }
      setStatus('ready');
    } catch (e) {
      setStatus('error');
    }
  };

  // First open: the newest month.
  useEffect(() => { loadMonth(null); }, [gameId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Search: debounced, newest request wins.
  const query = q.trim();
  useEffect(() => {
    if (query.length < 2) { setSearch({ q: '', days: null, truncated: false, loading: false }); return undefined; }
    const id = ++reqRef.current;
    setSearch((s) => ({ ...s, loading: true }));
    const t = setTimeout(async () => {
      try {
        const res = await fetch(url(`&q=${encodeURIComponent(query)}`));
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (id === reqRef.current) setSearch({ q: query, days: Array.isArray(data.days) ? data.days : [], truncated: !!data.truncated, loading: false });
      } catch (e) {
        if (id === reqRef.current) setSearch({ q: query, days: [], truncated: false, loading: false, error: true });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape closes · the page behind stops scrolling · focus goes to the search and back to the opener.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
      if (returnFocusTo && returnFocusTo.focus) returnFocusTo.focus();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pickMonth = (m) => {
    setQ('');
    setMonth(m);
    if (daysRef.current) daysRef.current.scrollTop = 0;
    if (!byMonth[m]) loadMonth(m);
  };
  const toggle = (k) => setOpen((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  const searching = query.length >= 2;
  const monthDays = byMonth[month] || [];
  const viewDays = searching ? (search.days || []) : monthDays;
  const repeats = repeatCount(viewDays);

  let body;
  if (searching) {
    const pending = search.loading || search.q !== query;
    const r = searchView(search.days, query, reps);
    if (search.error && !pending) body = <div className="hma-summary">Search didn't work. Try again in a moment.</div>;
    else if (!r.days.length && pending) body = <div className="hma-summary">Searching…</div>;
    else {
      body = r.days.length ? (
        <>
          <div className="hma-summary">
            {plural(r.n, 'memory', 'memories')} on {plural(r.days.length, 'day', 'days')} mention “{query}”{search.truncated ? '. Showing the newest days.' : ''}
          </div>
          {r.days.map((d) => <DayBlock key={d.day} day={d} open q={query} reps={reps} onToggle={() => {}} />)}
        </>
      ) : <div className="hma-summary">Nothing in the house's memories mentions “{query}”.</div>;
    }
  } else if (status === 'error' && !monthDays.length) {
    body = (
      <div className="hma-summary">
        The book won't open right now.
        <button type="button" className="hma-retry" onClick={() => loadMonth(month)}>Try again</button>
      </div>
    );
  } else if (status === 'loading' && !monthDays.length) {
    body = <div className="hma-summary">Opening the book…</div>;
  } else if (!monthDays.length) {
    body = <div className="hma-summary">Nothing written down yet.</div>;
  } else {
    body = monthDays.map((d) => <DayBlock key={d.day} day={d} open={open.has(d.day)} q="" reps={reps} onToggle={() => toggle(d.day)} />);
  }

  return ReactDOM.createPortal(
    <div className="hma">
      <style>{CSS}</style>
      <div className="hma-scrim" onClick={onClose} />
      <div className="hma-book" role="dialog" aria-modal="true" aria-labelledby="hma-title">
        <div className="hma-head">
          <h2 id="hma-title">
            📖 The house remembers
            {meta && meta.total > 0 && (
              <span className="hma-meta"> · {plural(meta.days, 'day', 'days')} · {plural(meta.total, 'memory', 'memories')}{meta.first_day ? ` · since ${longDay(meta.first_day)}` : ''}</span>
            )}
          </h2>
          <button type="button" className="hma-x" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="hma-tools">
          <label className="hma-search">
            <span aria-hidden="true">🔎</span>
            <input
              ref={inputRef}
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search: storm, movie night, a name"
              aria-label="Search the house's memories"
              autoComplete="off"
              maxLength={60}
            />
          </label>
          <div className={`hma-tabrow${searching ? ' hma-tabs-off' : ''}`} role="tablist" aria-label="Months">
            {(months || []).map((m) => (
              <button
                key={m.month}
                type="button"
                role="tab"
                className="hma-tab"
                aria-selected={!searching && m.month === month}
                onClick={() => pickMonth(m.month)}
              >{monthName(m.month)}<span>{plural(m.days, 'day', 'days')}</span></button>
            ))}
            {repeats > 0 && (
              <label className="hma-rep">
                <input type="checkbox" checked={reps} onChange={(e) => setReps(e.target.checked)} /> Show repeats ({repeats})
              </label>
            )}
          </div>
        </div>
        <div className="hma-days" ref={daysRef} role="tabpanel" aria-live="polite">{body}</div>
      </div>
    </div>,
    document.body,
  );
}
